import { useEffect, useState } from 'react';
import { amount } from '../lib/format';
import { useDisplayFeed, type DisplayLine } from '../lib/display';
import { Icon } from '../components/icons';
import { UpiQr } from '../components/UpiQr';
import { useT } from '../i18n';

/**
 * The customer display (route /display), for a second monitor that faces the queue.
 *
 * It mirrors the till: every item as it is scanned, what the customer is saving, the total, and
 * — when the cashier takes payment by UPI — the QR with the amount already in it, at a size a
 * phone can read across the counter. After the sale it thanks the customer and then goes back
 * to a welcome screen. It listens to the till over a BroadcastChannel and never calls the API,
 * so it needs no sign-in and holds no session (see lib/display.ts).
 *
 * Always light: it is read from a distance, often in a bright shop.
 */
export function CustomerDisplay() {
  const state = useDisplayFeed();
  const t = useT();
  const [showDone, setShowDone] = useState(true);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
    document.title = t('display.title');
  }, [t]);

  // The thank-you stays up long enough to read, then the screen goes back to its welcome.
  useEffect(() => {
    if (state?.kind !== 'done') return;
    setShowDone(true);
    const timer = window.setTimeout(() => setShowDone(false), 12_000);
    return () => window.clearTimeout(timer);
  }, [state]);

  const storeName = state?.storeName ?? t('display.welcome');

  if (!state) {
    return (
      <Screen>
        <Centered title={t('display.waiting')} subtitle={t('display.waitingHint')} />
      </Screen>
    );
  }

  if (state.kind === 'idle' || (state.kind === 'done' && !showDone)) {
    return (
      <Screen>
        <Centered title={t('display.welcomeTo', { store: storeName })} subtitle={t('display.idleHint')} />
      </Screen>
    );
  }

  if (state.kind === 'done') {
    return (
      <Screen>
        <div className="flex h-full flex-col items-center justify-center gap-6 p-10 text-center" data-testid="display-done">
          <span className="flex h-24 w-24 items-center justify-center rounded-full" style={{ background: '#e7f6ef', color: '#047857' }}>
            <Icon name="check" size={52} strokeWidth={2.6} />
          </span>
          <h1 className="text-5xl font-extrabold tracking-tight" style={{ color: '#0b1020' }}>
            {state.customerFirstName ? t('display.thanksName', { name: state.customerFirstName }) : t('display.thanks')}
          </h1>
          <p className="tabular text-2xl" style={{ color: '#4b5874' }}>
            {t('display.paid', { amount: amount(state.totalPaise) })}
            {state.changePaise > 0 ? ` · ${t('display.change', { amount: amount(state.changePaise) })}` : ''}
          </p>
          {state.savedPaise > 0 ? (
            <p className="rounded-full px-6 py-2 text-2xl font-bold" style={{ background: '#e7f6ef', color: '#047857' }}>
              {t('display.saved', { amount: amount(state.savedPaise) })}
            </p>
          ) : null}
          <p className="text-lg" style={{ color: '#7a869f' }}>
            {storeName} · {t('receipt.invoice', { number: state.invoiceNumber })}
          </p>
        </div>
      </Screen>
    );
  }

  const paying = state.kind === 'pay';
  return (
    <Screen>
      <div className="grid h-full grid-cols-[minmax(0,1fr)_minmax(24rem,34rem)]">
        <section className="flex min-h-0 flex-col gap-5 p-10">
          <header>
            <h1 className="text-4xl font-extrabold tracking-tight" style={{ color: '#0b1020' }}>
              {state.customerFirstName ? t('display.welcomeBack', { name: state.customerFirstName }) : t('display.welcomeTo', { store: storeName })}
            </h1>
            <p className="mt-1 text-lg" style={{ color: '#7a869f' }}>
              {storeName} · {t('display.onBill', { count: state.lines.length })}
            </p>
          </header>
          <ol className="min-h-0 flex-1 space-y-1 overflow-hidden rounded-3xl border bg-white p-3" style={{ borderColor: '#e3e8f1' }} data-testid="display-lines">
            {lastLines(state.lines).map(({ line, index }) => (
              <li
                key={`${index}-${line.name}`}
                className="flex items-center gap-4 rounded-2xl border px-5 py-3"
                style={index === state.lastIndex ? { background: '#eaf3ff', borderColor: '#0a5fae' } : { borderColor: 'transparent' }}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-2xl font-semibold" style={{ color: '#0b1020' }}>
                    {line.name}
                  </p>
                  <p className="tabular text-base" style={{ color: line.savedPaise > 0 ? '#047857' : '#7a869f' }}>
                    {line.detail}
                    {line.savedPaise > 0 ? ` · ${t('display.lineSave', { amount: amount(line.savedPaise) })}` : ''}
                  </p>
                </div>
                <p className="tabular text-2xl font-bold" style={{ color: '#0b1020' }}>
                  ₹{amount(line.amountPaise)}
                </p>
              </li>
            ))}
          </ol>
          <p className="text-base" style={{ color: '#7a869f' }}>
            {t('display.footer')}
          </p>
        </section>

        <aside className="flex flex-col items-center gap-6 p-10 text-center" style={{ background: '#0b1020', color: '#ffffff' }}>
          <div>
            <p className="text-sm font-bold tracking-[0.2em]" style={{ color: '#8391ab' }}>
              {paying ? t('display.totalToPay') : t('display.totalSoFar')}
            </p>
            <p className="tabular text-7xl font-extrabold tracking-tight" data-testid="display-total">
              ₹{amount(state.totalPaise)}
            </p>
            {state.savedPaise > 0 ? (
              <p className="mt-3 inline-flex rounded-full px-4 py-1.5 text-lg font-semibold" style={{ background: '#0e2e25', color: '#34d399' }}>
                {t('display.saving', { amount: amount(state.savedPaise) })}
              </p>
            ) : null}
          </div>
          {paying && state.upiUri ? (
            <div className="flex flex-col items-center gap-3 rounded-3xl bg-white p-5" style={{ color: '#0b1020' }} data-testid="display-qr">
              <UpiQr value={state.upiUri} size={280} label={t('display.scanToPay', { amount: amount(state.upiAmountPaise) })} />
              <p className="text-xl font-bold">{t('display.anyUpi')}</p>
              <p className="tabular text-base" style={{ color: '#4b5874' }}>
                {t('display.to', { amount: amount(state.upiAmountPaise), payee: state.upiPayee ?? '' })}
              </p>
            </div>
          ) : paying ? (
            <p className="text-xl" style={{ color: '#b6c0d3' }}>
              {t('display.taking')}
            </p>
          ) : null}
          {paying && state.upiUri ? (
            <p className="text-base" style={{ color: '#b6c0d3' }}>
              {t('display.filled')}
            </p>
          ) : null}
        </aside>
      </div>
    </Screen>
  );
}

/** The last items that fit, newest at the bottom: a long bill scrolls itself like a till roll. */
function lastLines(lines: DisplayLine[]): { line: DisplayLine; index: number }[] {
  const start = Math.max(0, lines.length - 8);
  return lines.slice(start).map((line, offset) => ({ line, index: start + offset }));
}

function Screen({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full overflow-hidden" style={{ background: '#f7f9fc', fontFamily: 'var(--font-sans)' }} data-testid="customer-display">
      {children}
    </div>
  );
}

function Centered({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 p-10 text-center">
      <span className="flex h-20 w-20 items-center justify-center rounded-3xl" style={{ background: '#0a5fae', color: '#fff' }}>
        <Icon name="cart" size={42} strokeWidth={1.9} />
      </span>
      <h1 className="text-5xl font-extrabold tracking-tight" style={{ color: '#0b1020' }}>
        {title}
      </h1>
      <p className="max-w-2xl text-xl" style={{ color: '#4b5874' }}>
        {subtitle}
      </p>
    </div>
  );
}
